import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Upload, ImageOff } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { getMedias, uploadMedia } from '@/services/api/meta-media';
import type { MediaResponse } from '@/types/media';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSelect: (url: string) => void;
  pageId: string; // necessário pro upload
}

export default function MediaLibraryDialog({ open, onOpenChange, onSelect, pageId }: Props) {
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [uploading, setUploading] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['builder', 'media', 'image'],
    queryFn: () => getMedias({ media_type: 'image' }),
    enabled: open,
  });
  const items = (data ?? []).filter(
    (m: MediaResponse) => !q.trim() || (m.filename ?? '').toLowerCase().includes(q.toLowerCase()),
  );

  const onUpload = async (file: File | undefined) => {
    if (!file) return;
    if (!pageId) { toast({ title: 'Escolha a página antes de subir mídia', variant: 'destructive' }); return; }
    setUploading(true);
    try {
      const item = await uploadMedia({ file, page_id: pageId });
      if (item.file_url) { onSelect(item.file_url); onOpenChange(false); }
      refetch();
    } catch (e: any) {
      toast({ title: 'Falha no upload', description: e?.response?.data?.detail || String(e?.message ?? e), variant: 'destructive' });
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Selecionar mídia</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2">
          <Input placeholder="Buscar imagem…" value={q} onChange={(e) => setQ(e.target.value)} />
          <label className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border px-3 text-sm hover:bg-muted">
            <Upload className="h-4 w-4" /> {uploading ? '…' : 'Enviar'}
            <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => onUpload(e.target.files?.[0])} />
          </label>
        </div>
        <div className="grid max-h-[55vh] grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4">
          {isLoading ? (
            Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-square animate-pulse rounded-lg bg-slate-100" />)
          ) : items.length === 0 ? (
            <div className="col-span-full flex flex-col items-center gap-1 py-10 text-sm text-muted-foreground">
              <ImageOff className="h-6 w-6" /> Nenhuma imagem na biblioteca.
            </div>
          ) : (
            items.map((m: MediaResponse) => (
              <button
                key={m.id}
                onClick={() => { if (m.file_url) { onSelect(m.file_url); onOpenChange(false); } }}
                className="group relative aspect-square overflow-hidden rounded-lg border bg-slate-50 transition hover:ring-2 hover:ring-indigo-300"
                title={m.filename}
              >
                <img src={m.file_url} alt="" className="h-full w-full object-cover" />
              </button>
            ))
          )}
        </div>
        <div className="flex justify-end">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
